/*
 * 苏州海管家物流科技有限公司（https://www.hgj.com/）.
 * 苏公网安备32059002003385号 © 2015-2023 Hgj.com All Rights Reserved. | 苏ICP备15030950号.
 * @Date: 2022.07.31.
 * @Author
 * @Description 企微、企点 - 项目接口封装 - 根据请求类型封装 .
 **/
import axios, { AxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/store/authStore'
import { E_HTTP_METHOD } from '@/typings/type'
import request, { singleRequest } from './request'
import urls from './urls'
import { ElMessage } from '@hgj/element-plus'
import { useProxyInsurance } from '@/hooks/useProxyInsurance'
import { APP_NAME, expandHeader, isGlobal } from '@/utils/constant'
export type VerifyCodeReq = {
  captcha: {
    ticket: string
    randstr: string
  }
  phone: string
}
/**
 *@Description
 *@Return 全局post，处理catch返回，不忽略
 **/
export const wholePost = (url: string, params: any) => {
  const baseURL = import.meta.env.VITE_BASE_URL
  // 动态获取代投保请求头（确保非代投保企业时不添加）
  const { getApprovalAgentHeader } = useProxyInsurance()
  return axios({
    method: 'post',
    url: baseURL + url,
    headers: {
      'access-token': useAuthStore().getToken,
      'app-name': 'sass-insurance-web',
      ...getApprovalAgentHeader(),
      ...expandHeader,
    },
    data: params,
  })
}

/**
 *@Description
 *@Return get 方式请求 方法.
 **/
export function getData(url: string, params?: any) {
  return request({
    url,
    params,
    method: E_HTTP_METHOD.GET,
  })
}
/**
 *@Description - 涵盖 文件上传
 *@Return post 方式请求 方法.
 **/
export function postData(
  url: string,
  data?: Record<string, unknown> | FormData,
  param?: any,
): DataResponse<any> {
  const obj: AxiosRequestConfig = {
    url,
    data,
    method: E_HTTP_METHOD.POST,
  }

  if (param && param.type === 'export') {
    obj.responseType = 'arraybuffer'
  }

  if (param && param.type === 'import') {
    obj.headers = {
      'Content-Type': 'multipart/form-data',
    }
  }

  return request({ ...obj })
}
/**
 *@Description 涵盖 导出、表单提交
 *@Return post 方式 有中断方式 请求 方法 = Record<string, unknown> | FormData | string.
 **/
export const postSingleData = async (
  url: string,
  data?: any,
  param?: Record<string, string>,
) => {
  const obj: any = {
    url,
    data,
    method: E_HTTP_METHOD.POST,
  }
  if (param && param.type === 'export') {
    obj.responseType = 'arraybuffer'
  }
  if (param && param.type === 'form-data') {
    obj.headers = {
      'Content-Type': 'multipart/form-data',
    }
  }

  return await singleRequest({ ...obj })
}
/**
 *@Description
 *@Return put 方式请求 方法.
 **/
export function putData(url: string, data?: Record<string, unknown> | any) {
  return request({
    url,
    data,
    method: E_HTTP_METHOD.PUT,
  })
}
/**
 *@Description
 *@Return delete 方式请求 方法.
 **/
export function deleteData(url: string, data?: any) {
  return request({
    url,
    data,
    method: E_HTTP_METHOD.DELETE,
  })
}
/**
 *@Description
 *@Return patch 方式请求 方法.
 **/
export function patchData(url: string, data?: Record<string, unknown>) {
  return request({
    url,
    data,
    method: E_HTTP_METHOD.PATCH,
  })
}

/**
 *@Description
 *@Return 获取中台用户数据
 **/
export const getPublicUser = () => {
  const baseURL = import.meta.env.VITE_APP_AUTH
  const { getApprovalAgentHeader } = useProxyInsurance()
  return axios({
    method: 'get',
    url:
      baseURL +
      '/whale-user-center/whale/user/get-user-detail-info?source=public-head',
    headers: {
      'access-token': useAuthStore().getToken,
      'app-name': 'sass-insurance-web',
      ...getApprovalAgentHeader(),
      ...expandHeader,
    },
  })
}

/**
 * @description 获取短信验证码
 * @return {Promise<DataResponse<Record<string, DictRaw>>>}
 */
// export const fetchPhoneCode = async (
//     data: VerifyCodeReq,
//   ) => {
//     const res = await request.post<DataResponse<any>>(
//       '/job/send/verifyCode',
//       data,
//     )
//     return res.data
// }

export const downloadTemplate = async (
  businessType: string = 'MANIFEST',
  type: 'S',
  port: string = '',
) => {
  const res: any = await request.get<DataResponse<any>>(
    '/all-port/storybrooke-assistor/pass/template/findByBusinessType',
    {
      params: {
        appName: '货运保险',
        port,
        businessType,
        type,
      },
    },
  )
  const { fileId } = res.data[0] || {}
  if (fileId) {
    const url = isGlobal
      ? `${import.meta.env.VITE_APP_NAS_PREV_URL}/pass/proxy/${fileId}`
      : `https:${import.meta.env.VITE_APP_NAS_PREV_URL}/pass/proxy/${fileId}`
    location.href = url
  } else {
    ElMessage.error(`模板${businessType}未配置`)
  }
}

export const getHgjTemplate = async (
  category: string,
  channel: string = 'KB',
  templateCode: string = '',
) => {
  const res: any = await request.get<DataResponse<any>>(
    '/all-port/storybrooke-assistor/template/getBusiness',
    {
      params: {
        category,
        channel,
        templateCode,
        appName: 'saas-insurance',
      },
    },
  )
  return res.data
}

export const downloadHgjTemplate = async (data: any) => {
  const res: any = await request.post<DataResponse<any>>(
    '/all-port/storybrooke-assistor/template/downloadBusiness',
    data,
    {
      responseType: 'blob',
    },
  )
  return res
}

/**
 * 获取业务资料
 * @param params
 * @returns
 */
export const fetchBusinessDataList = async (params: { type: string }) => {
  const res: any = await request.get<DataResponse<any>>(
    '/all-port/storybrooke-assistor/business-data/list',
    {
      params: {
        pageNo: 1,
        pageSize: 999,
        appName: '货运保险',
        ...params,
      },
    },
  )
  return res.data
}

export const fetchUserGuideFile = async (supplyCode: string) => {
  const res: any = await request.get<DataResponse<any>>(urls.getUserGuideUrl, {
    params: {
      appName: '货运保险',
      port: '',
      type: `use_guide_${supplyCode}`,
    },
  })
  return res.data
}
